"use client"

import { FormEvent, useRef, useEffect, useState } from "react"
import { ArrowUp, Paperclip, X } from "lucide-react"
import {
  PromptInput,
  PromptInputRow,
  PromptInputSubmit,
  PromptInputTextarea,
} from "@/components/ai-elements/prompt-input"
import { copy } from "@/lib/copy"

interface ChatInputProps {
  input: string
  setInput: (input: string) => void
  handleSubmit: (e: FormEvent, files: File[]) => void
  isLoading: boolean
  onStop?: () => void
  className?: string
}

// Accepted attachment types. Images go to the vision lane (the model sees the
// pixels); documents (PDF/Word/txt/md) are read by the model; spreadsheets
// (Excel/CSV) are analyzed in the Code Interpreter sandbox. Kept in lockstep
// with the backend allow-list (presign Lambda / app.py router).
const ACCEPT =
  "image/png,image/jpeg,image/webp,image/gif,.pdf,.docx,.txt,.md,.csv,.xlsx,.xls"

// Per-turn attachment caps, surfaced in the attach tooltip and enforced here.
// 3.75 MB is Bedrock's per-image ceiling and stays under the 4.5 MB document
// limit, so one uniform cap is always valid; 5 files bounds tokens/latency.
const MAX_FILES = 5
const MAX_FILE_BYTES = 3_750_000

/** Human-readable file size for an attachment chip. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Whether a staged file is an image we can preview as a thumbnail. */
function isImageFile(file: File): boolean {
  return file.type.startsWith("image/")
}

/**
 * Conversation composer: a single-row pill (ChatGPT-style) with the attach
 * control on the left, the input in the middle, and a circular send/stop control
 * on the right. Any attachments appear as chips above the pill.
 *
 * Attached images are sent with the turn as multi-modal content so the model
 * can see them; the staged files are handed to `handleSubmit` and cleared after.
 *
 * @param input - current text value.
 * @param setInput - setter for the text value.
 * @param handleSubmit - form submit handler; receives the event and the staged
 *   image files for this turn.
 * @param isLoading - whether a response is in flight (drives send/stop state).
 * @param onStop - cancels the in-flight response.
 * @param className - extra classes merged onto the wrapper.
 */
export function ChatInput({
  input,
  setInput,
  handleSubmit,
  isLoading,
  onStop,
  className = "",
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [attachNotice, setAttachNotice] = useState<string | null>(null)

  // Object URLs for image thumbnails, keyed to the staged files by index. Built
  // from `files` and revoked on change/unmount so blob URLs never leak.
  const [previewUrls, setPreviewUrls] = useState<(string | null)[]>([])
  useEffect(() => {
    const urls = files.map(f => (isImageFile(f) ? URL.createObjectURL(f) : null))
    setPreviewUrls(urls)
    return () => {
      urls.forEach(u => {
        if (u) URL.revokeObjectURL(u)
      })
    }
  }, [files])

  // Auto-resize the textarea to fit its content (bounded by max-height in CSS).
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = "0px"
      textarea.style.height = `${textarea.scrollHeight}px`
    }
  }, [input])

  // Return focus to the composer when a turn finishes (isLoading true → false),
  // so the user can keep typing without a click. The textarea is disabled while
  // streaming, so it loses focus mid-turn; re-enabling it does not refocus on
  // its own. Guarded so we never yank focus away from where the user actually
  // put it: only refocus when nothing meaningful holds focus (body/root), and
  // never on touch devices, where popping the on-screen keyboard would be worse.
  const prevLoadingRef = useRef(isLoading)
  useEffect(() => {
    const turnFinished = prevLoadingRef.current && !isLoading
    prevLoadingRef.current = isLoading
    if (!turnFinished) return
    const active = document.activeElement
    const focusIsIdle = active === null || active === document.body || active === document.documentElement
    const isCoarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches ?? false
    if (focusIsIdle && !isCoarsePointer) {
      textareaRef.current?.focus()
    }
  }, [isLoading])

  const handlePickFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? [])
    // Reset so picking the same file again still fires onChange.
    event.target.value = ""
    if (!picked.length) return

    // Drop oversize files, then cap the total count per turn. Surface a single
    // notice covering whatever was omitted.
    const withinSize = picked.filter(f => f.size <= MAX_FILE_BYTES)
    const droppedForSize = withinSize.length < picked.length

    setFiles(prev => {
      const room = Math.max(0, MAX_FILES - prev.length)
      const accepted = withinSize.slice(0, room)
      const droppedForCount = withinSize.length > room
      if (droppedForSize && droppedForCount) {
        setAttachNotice(`${copy.input.attachTooLarge} ${copy.input.attachTooMany}`)
      } else if (droppedForSize) {
        setAttachNotice(copy.input.attachTooLarge)
      } else if (droppedForCount) {
        setAttachNotice(copy.input.attachTooMany)
      } else {
        setAttachNotice(null)
      }
      return [...prev, ...accepted]
    })
  }

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
    setAttachNotice(null)
  }

  // Send the turn with any staged image files, then clear the staging area.
  const onSubmit = (event: FormEvent) => {
    handleSubmit(event, files)
    setFiles([])
    setAttachNotice(null)
  }

  return (
    <div className={`w-full p-4 ${className}`}>
      {attachNotice && (
        <p className="mb-2 px-1 text-xs text-muted-foreground" role="status">
          {attachNotice}
        </p>
      )}

      <PromptInput onSubmit={onSubmit}>
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 px-1 pt-1">
            {files.map((file, index) => {
              const preview = previewUrls[index]
              // Image: thumbnail with an X badge in the corner (Figma).
              if (preview) {
                return (
                  <div
                    key={`${file.name}-${index}`}
                    className="group relative size-28 overflow-hidden rounded-xl border border-chart-card-border bg-muted"
                  >
                    <img
                      src={preview}
                      alt={file.name}
                      className="size-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      aria-label={`Quitar ${file.name}`}
                      className="absolute right-1.5 top-1.5 inline-flex size-6 items-center justify-center rounded-full bg-card/80 text-foreground shadow-sm backdrop-blur transition-colors hover:bg-card focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    >
                      <X className="size-3.5" aria-hidden="true" />
                    </button>
                  </div>
                )
              }
              // Non-image (PDF/Excel/…): keep the text chip.
              return (
                <span
                  key={`${file.name}-${index}`}
                  className="inline-flex items-center gap-1.5 self-center rounded-full bg-secondary px-2.5 py-1 text-xs text-foreground ring-1 ring-inset ring-border/60"
                >
                  <Paperclip className="size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="max-w-[180px] truncate">{file.name}</span>
                  <span className="text-muted-foreground/70">{formatSize(file.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    aria-label={`Quitar ${file.name}`}
                    className="rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    <X className="size-3" aria-hidden="true" />
                  </button>
                </span>
              )
            })}
          </div>
        )}

        <PromptInputRow>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={handlePickFiles}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            aria-label={copy.input.attach}
            title={copy.input.attach}
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-background text-foreground shadow-sm transition-colors hover:bg-background/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
          >
            <Paperclip className="size-5" aria-hidden="true" />
          </button>

          <PromptInputTextarea
            ref={textareaRef}
            value={input}
            onValueChange={setInput}
            onSubmitShortcut={() => onSubmit({ preventDefault: () => {} } as FormEvent)}
            placeholder={copy.input.placeholder}
            disabled={isLoading}
            aria-label={copy.input.placeholder}
            autoFocus
            className="flex-1 overflow-y-auto px-2 py-1.5"
          />

          <PromptInputSubmit
            status={isLoading ? "streaming" : "ready"}
            label={copy.input.send}
            onStop={onStop}
            stopLabel={copy.input.stop}
            disabled={!input.trim() || isLoading}
            className="size-9"
          >
            <ArrowUp className="size-5" aria-hidden="true" />
          </PromptInputSubmit>
        </PromptInputRow>
      </PromptInput>
    </div>
  )
}
